<template>
  <main>
    <section>
      <img class="logo" src="/logos/logo-nuteam-white.svg" alt="NuTeam Logo" />
      <div class="content">
        <div class="icon-container">
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
            />
            <polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="2" />
          </svg>
        </div>

        <h1>{{ emailResent ? 'Email de vérification renvoyé' : 'Vérifiez votre email' }}</h1>
        <p class="subtitle">
          {{
            emailResent
              ? 'Un nouvel email de vérification a été envoyé à votre adresse.'
              : 'Un email de vérification a été envoyé à votre adresse email.'
          }}
        </p>

        <div class="email-info">
          <strong>{{ email }}</strong>
        </div>

        <div class="instructions">
          <p>Voici comment procéder :</p>
          <ul>
            <li>Consultez votre boîte email (y compris les spams)</li>
            <li>Cliquez sur le lien de vérification dans l'email</li>
            <li>Vous serez automatiquement connecté à votre compte</li>
          </ul>
        </div>

        <div class="actions">
          <button
            @click="resendVerificationEmail"
            :disabled="isResending || cooldownActive"
            class="btn btn-secondary"
          >
            <span v-if="isResending">Envoi en cours...</span>
            <span v-else-if="cooldownActive"> Renvoyer dans {{ cooldownTime }}s </span>
            <span v-else>Renvoyer l'email</span>
          </button>

          <router-link to="/login" class="btn btn-primary"> Retour à la connexion </router-link>
        </div>

        <div v-if="resendMessage" class="message success">
          {{ resendMessage }}
        </div>

        <div v-if="resendError" class="message error">
          {{ resendError }}
        </div>
      </div>
    </section>
    <section class="img-container">
      <span class="badge version">
        {{ $t('ui.version') }}
        <strong>{{ version }}</strong>
      </span>
    </section>
  </main>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { kyWithAuth } from '../libs/ky';
import { version } from '../../package';
const route = useRoute();

const email = ref(route.query.email || '');
const emailResent = ref(route.query.resent === 'true');
const isResending = ref(false);
const cooldownActive = ref(false);
const cooldownTime = ref(0);
const resendMessage = ref('');
const resendError = ref('');

let cooldownInterval = null;

const resendVerificationEmail = async () => {
  if (isResending.value || cooldownActive.value) return;

  try {
    isResending.value = true;
    resendError.value = '';

    await kyWithAuth
      .post('users/resend-verification', {
        json: { email: email.value }
      })
      .json();

    resendMessage.value = 'Email de vérification renvoyé avec succès !';
    startCooldown();
  } catch (err) {
    console.error('Resend verification error:', err);
    resendError.value = "Erreur lors du renvoi de l'email. Veuillez réessayer.";
  } finally {
    isResending.value = false;
  }
};

const startCooldown = () => {
  cooldownActive.value = true;
  cooldownTime.value = 60;

  cooldownInterval = setInterval(() => {
    cooldownTime.value--;
    if (cooldownTime.value <= 0) {
      cooldownActive.value = false;
      clearInterval(cooldownInterval);
    }
  }, 1000);
};

onMounted(() => {
  if (!email.value) {
    // Si pas d'email en query, essayer de le récupérer du localStorage ou rediriger
    const storedEmail = localStorage.getItem('pendingVerificationEmail');
    if (storedEmail) {
      email.value = storedEmail;
      localStorage.removeItem('pendingVerificationEmail');
    }
  }
});

onUnmounted(() => {
  if (cooldownInterval) {
    clearInterval(cooldownInterval);
  }
});
</script>

<style scoped lang="scss">
#app > main {
  padding-bottom: 0px;
}

main {
  display: flex;
  min-height: 100vh;
}

.content {
  text-align: center;
  max-width: 500px;
}

.icon-container {
  margin: 2rem 0;
  color: $brand-light-indigo;
  svg {
    width: 80px;
    height: 80px;
  }
}

h1 {
  margin: 15px 0 20px;
  color: lightgrey;
  font-size: 28px;
}

.subtitle {
  color: rgba($white, 0.8);
  font-size: 16px;
  margin-bottom: 2rem;
  line-height: 1.5;
}

.email-info {
  background-color: rgba($brand-light-indigo, 0.1);
  border: 1px solid $brand-light-indigo;
  border-radius: $base-radius;
  padding: 1rem;
  margin: 1.5rem 0;
  color: $brand-light-indigo;
  font-size: 16px;
}

.instructions {
  text-align: left;
  background-color: rgba($white, 0.05);
  border-radius: $base-radius;
  padding: 1.5rem;
  margin: 2rem 0;

  p {
    color: rgba($white, 0.9);
    margin-bottom: 1rem;
    font-weight: 500;
  }

  ul {
    color: rgba($white, 0.7);
    padding-left: 1.2rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.4;
    }
  }
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 2rem 0;
}

.btn {
  padding: 12px 24px;
  border-radius: $base-radius;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  text-align: center;
  border: none;
  cursor: pointer;
  transition: all 200ms ease-in-out;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.btn-primary {
    background-color: $brand-corail;
    color: $white;

    &:hover:not(:disabled) {
      background-color: darken($brand-corail, 10%);
    }
  }

  &.btn-secondary {
    background-color: transparent;
    color: $brand-light-indigo;
    border: 1px solid $brand-light-indigo;

    &:hover:not(:disabled) {
      background-color: rgba($brand-light-indigo, 0.1);
    }
  }
}

.message {
  padding: 12px 16px;
  border-radius: $base-radius;
  margin-top: 1rem;
  font-size: 14px;

  &.success {
    background-color: rgba($brand-mint, 0.1);
    border: 1px solid $brand-mint;
    color: $brand-mint;
  }

  &.error {
    background-color: rgba($brand-corail, 0.1);
    border: 1px solid $brand-corail;
    color: $brand-corail;
  }
}

.logo {
  width: 150px;
  margin-bottom: 2rem;
}

.badge {
  border-radius: 20px;
  background-color: rgba($white, 0.6);
  color: $brand-dark-purple;
  border: 1px solid $brand-dark-purple;
  padding: 3px 18px;
  font-size: 9px;
  position: absolute;
  bottom: 18px;
  right: 18px;
  text-transform: uppercase;
}

main > section:not(.img-container) {
  padding: 50px 80px;
  width: 850px;
  background-color: $brand-dark-grey-purple;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

main > .img-container {
  width: 100%;
  min-height: 100%;
  overflow: hidden;
  background-image: url('../../public/svg/illustration_login.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 90%;
  background-color: #f7f8fd;
  box-shadow: -5px 0px 10px 0px rgba($black, 0.1);
  position: relative;
}

@media (min-width: 1440px) {
  main > .img-container {
    background-size: 70%;
  }
}

@media (max-width: 1023px) {
  main {
    display: grid;
    grid-template-rows: auto 1fr;
  }
  main > section:not(.img-container) {
    width: 100%;
  }
  main > .img-container {
    background-size: 55%;
  }
}

@media (max-width: 767px) {
  main > .img-container {
    background-size: 75%;
  }
}
</style>
